/**
 * Finding analysis — pattern detection, rollup, and condition-level flagging.
 *
 * Maps CMS standard references to their parent CfC conditions and detects
 * when multiple findings under the same standard or condition suggest a
 * pattern (severity upgrade) or condition-level deficiency.
 */

import type { SurveyDomain, SeverityLevel } from './services/survey'

// ── Types ───────────────────────────────────────────────────────────────────

export interface AnalyzableFinding {
  finding_id: string
  domain: SurveyDomain
  standard_ref: string
  severity: SeverityLevel
  observation: string
  area: string
}

export interface PatternAlert {
  /** The standard reference with multiple findings */
  standardRef: string
  /** Number of findings under this standard */
  count: number
  /** The finding IDs */
  findingIds: string[]
  /** Areas where findings occurred */
  areas: string[]
  /** Whether severity upgrade from 1→2 is suggested */
  suggestUpgrade: boolean
  /** Human-readable description */
  description: string
}

export interface ConditionAlert {
  /** CfC section (e.g., "§416.51") */
  cfcSection: string
  /** CfC title (e.g., "Infection Control") */
  cfcTitle: string
  /** Domain */
  domain: SurveyDomain
  /** Total findings under this CfC */
  findingCount: number
  /** Standard refs involved */
  standardRefs: string[]
  /** Finding IDs */
  findingIds: string[]
  /** Whether any finding is severity 3 (immediate jeopardy) */
  hasImmediateJeopardy: boolean
  /** Severity level of the condition-level concern */
  severity: 'warning' | 'critical'
  /** Human-readable description */
  description: string
}

// ── CfC Mapping ─────────────────────────────────────────────────────────────
// Maps CMS CfC sections to their standard references and domains.
// Based on 42 CFR 416.25–416.52 (ASC Conditions for Coverage)

interface CfcCondition {
  cfcSection: string
  cfcTitle: string
  domain: SurveyDomain
  /** Standard reference prefixes that fall under this CfC */
  standardPrefixes: string[]
}

const CFC_CONDITIONS: CfcCondition[] = [
  {
    cfcSection: '§416.41',
    cfcTitle: 'Governing Body & Management',
    domain: 'COMP',
    standardPrefixes: ['§416.41', 'LD.'],
  },
  {
    cfcSection: '§416.42',
    cfcTitle: 'Surgical Services',
    domain: 'CR',
    standardPrefixes: ['§416.42', 'PC.', 'UP.'],
  },
  {
    cfcSection: '§416.43',
    cfcTitle: 'Quality Assessment & Performance Improvement',
    domain: 'COMP',
    standardPrefixes: ['§416.43', 'PI.'],
  },
  {
    cfcSection: '§416.44',
    cfcTitle: 'Environment / Physical Environment',
    domain: 'ENV',
    standardPrefixes: ['§416.44', 'EC.', 'LS.'],
  },
  {
    cfcSection: '§416.45',
    cfcTitle: 'Medical Staff',
    domain: 'HR',
    standardPrefixes: ['§416.45', 'MS.', 'HR.'],
  },
  {
    cfcSection: '§416.46',
    cfcTitle: 'Nursing Services',
    domain: 'HR',
    standardPrefixes: ['§416.46'],
  },
  {
    cfcSection: '§416.48',
    cfcTitle: 'Pharmaceutical Services',
    domain: 'MED',
    standardPrefixes: ['§416.48', 'MM.', 'NPSG.03'],
  },
  {
    cfcSection: '§416.50',
    cfcTitle: 'Patient Rights',
    domain: 'COMP',
    standardPrefixes: ['§416.50', '§416.42(a)', '§416.43', '§416.44', 'RI.'],
  },
  {
    cfcSection: '§416.51',
    cfcTitle: 'Infection Control',
    domain: 'IC',
    standardPrefixes: ['§416.51', 'IC.', 'NPSG.07'],
  },
  {
    cfcSection: '§416.52',
    cfcTitle: 'Patient Admission, Assessment & Discharge',
    domain: 'CR',
    standardPrefixes: ['§416.52', 'PC.03', 'PC.04'],
  },
  {
    cfcSection: '§416.44(b)',
    cfcTitle: 'Life Safety Code',
    domain: 'LSC',
    standardPrefixes: ['§416.44(b)', 'LS.', 'NFPA'],
  },
  {
    cfcSection: '§416.41(b)',
    cfcTitle: 'Emergency Management',
    domain: 'EM',
    standardPrefixes: ['§416.41(b)', 'EM.'],
  },
]

// ── Analysis Functions ──────────────────────────────────────────────────────

/** Group findings by their standard reference */
export function groupFindingsByStandard(
  findings: AnalyzableFinding[],
): Map<string, AnalyzableFinding[]> {
  const groups = new Map<string, AnalyzableFinding[]>()
  for (const f of findings) {
    const ref = f.standard_ref.trim()
    if (!ref) continue
    const existing = groups.get(ref) ?? []
    existing.push(f)
    groups.set(ref, existing)
  }
  return groups
}

/**
 * Detect patterns — standards with 3+ findings suggest a systemic issue.
 * Returns alerts for standards that warrant severity review.
 */
export function detectPatterns(findings: AnalyzableFinding[]): PatternAlert[] {
  const groups = groupFindingsByStandard(findings)
  const alerts: PatternAlert[] = []

  for (const [ref, group] of groups) {
    if (group.length < 2) continue // Need at least 2 to suggest a pattern

    const hasLowSeverity = group.some((f) => f.severity === 1)
    const areas = [...new Set(group.map((f) => f.area))]

    alerts.push({
      standardRef: ref,
      count: group.length,
      findingIds: group.map((f) => f.finding_id),
      areas,
      suggestUpgrade: hasLowSeverity && group.length >= 3,
      description:
        group.length >= 3
          ? `${group.length} findings under ${ref} across ${areas.length} area(s) — this may indicate a pattern of noncompliance (consider Moderate severity).`
          : `${group.length} findings under ${ref} — monitor for emerging pattern.`,
    })
  }

  return alerts.sort((a, b) => b.count - a.count)
}

/**
 * Find the parent CfC condition for a standard reference.
 * Checks standard_ref against known CfC prefix patterns.
 */
function findParentCfc(standardRef: string): CfcCondition | undefined {
  const ref = standardRef.trim()
  for (const cfc of CFC_CONDITIONS) {
    if (cfc.standardPrefixes.some((prefix) => ref.startsWith(prefix))) {
      return cfc
    }
  }
  return undefined
}

/**
 * Detect condition-level deficiency concerns.
 * Triggers when:
 * - 3+ findings under the same CfC parent condition
 * - Any finding is severity 3 (immediate jeopardy)
 * - Findings span multiple standards under the same CfC (systemic)
 */
export function detectConditionLevel(findings: AnalyzableFinding[]): ConditionAlert[] {
  // Group findings by parent CfC
  const cfcGroups = new Map<string, { cfc: CfcCondition; findings: AnalyzableFinding[] }>()

  for (const f of findings) {
    const parentCfc = findParentCfc(f.standard_ref)
    if (!parentCfc) continue

    const key = parentCfc.cfcSection
    const existing = cfcGroups.get(key)
    if (existing) {
      existing.findings.push(f)
    } else {
      cfcGroups.set(key, { cfc: parentCfc, findings: [f] })
    }
  }

  const alerts: ConditionAlert[] = []

  for (const [, { cfc, findings: cfcFindings }] of cfcGroups) {
    const hasIJ = cfcFindings.some((f) => f.severity === 3)
    const standardRefs = [...new Set(cfcFindings.map((f) => f.standard_ref))]
    const isSystemic = standardRefs.length >= 2 && cfcFindings.length >= 3

    // Only alert if there's a meaningful concern
    if (!hasIJ && cfcFindings.length < 3) continue

    const severity = hasIJ ? 'critical' as const : 'warning' as const

    let description: string
    if (hasIJ) {
      description = `IMMEDIATE JEOPARDY: ${cfcFindings.length} finding(s) under ${cfc.cfcSection} (${cfc.cfcTitle}) include a severity 3 deficiency. This may constitute a condition-level deficiency requiring immediate corrective action.`
    } else if (isSystemic) {
      description = `${cfcFindings.length} findings across ${standardRefs.length} standards under ${cfc.cfcSection} (${cfc.cfcTitle}). Pattern suggests the entire condition may not be met — consider condition-level deficiency determination.`
    } else {
      description = `${cfcFindings.length} findings under ${cfc.cfcSection} (${cfc.cfcTitle}). Review whether this rises to condition-level noncompliance.`
    }

    alerts.push({
      cfcSection: cfc.cfcSection,
      cfcTitle: cfc.cfcTitle,
      domain: cfc.domain,
      findingCount: cfcFindings.length,
      standardRefs,
      findingIds: cfcFindings.map((f) => f.finding_id),
      hasImmediateJeopardy: hasIJ,
      severity,
      description,
    })
  }

  return alerts.sort((a, b) => {
    // Critical first, then by finding count
    if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1
    return b.findingCount - a.findingCount
  })
}

/**
 * Get all CfC conditions for reference display.
 */
export function getCfcConditions(): CfcCondition[] {
  return CFC_CONDITIONS
}
